import React, { useEffect, useState, useCallback } from "react";
import { Download, FileSpreadsheet, Search, ChevronLeft, ChevronRight, MessageCircle, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, fetchTransactionsCsv } from "@/lib/api";
import { exportLedgerXlsx, waInvoiceUrl } from "@/lib/exporters";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDate, todayISO, monthStartISO, CATEGORIES } from "@/lib/format";
import { PageHeader, Card, Loading, Pill, Button, EmptyState, SeverityTag, Modal, Field } from "@/components/kit";
import TransactionDetail from "@/components/TransactionDetail";

const LIMIT = 25;

export default function Ledger() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const [range, setRange] = useState({ from: monthStartISO(), to: todayISO() });
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [txnType, setTxnType] = useState("all");
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ loading: true, rows: [], total: 0, sum: 0 });
  const [detail, setDetail] = useState(null);
  const [payRow, setPayRow] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      if (isOwner) {
        const r = await api.ownerAnalyticsTransactions({ from: range.from, to: range.to, q, category, txn_type: txnType, page, limit: LIMIT });
        setState({ loading: false, rows: r.rows, total: r.total, sum: r.sum });
      } else {
        const r = await api.adminLineTransactions({ from: range.from, to: range.to, q, category, txn_type: txnType });
        const rows = (r.transactions || []).map((t) => ({
          id: t.id, member_id: t.member_id, member_name: t.member_name || t.name || "Member",
          category: t.category, txn_type: t.txn_type, amount: t.amount, note: t.note,
          created_at: t.transaction_at || t.created_at, transaction_at: t.transaction_at, class_name: t.class_name, court: t.court,
          bill_id: t.bill_id, bill_total: t.bill_total, bill_paid: t.bill_paid, bill_remaining: t.bill_remaining, bill_status: t.bill_status, payment_type: t.payment_type,
        }));
        setState({ loading: false, rows, total: r.count, sum: r.total });
      }
    } catch (e) { toast.error(e.message); setState({ loading: false, rows: [], total: 0, sum: 0 }); }
  }, [isOwner, range, q, category, txnType, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [range, q, category, txnType]);

  const exportCsv = async () => {
    try {
      const csv = await fetchTransactionsCsv({ from: range.from, to: range.to, category, txn_type: txnType });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `transaksi_${range.from}_${range.to}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV terunduh");
    } catch (e) { toast.error(e.message); }
  };

  const exportXlsx = async () => {
    try {
      let rows = [];
      if (isOwner) {
        const r = await api.ownerAnalyticsTransactions({ from: range.from, to: range.to, q, category, txn_type: txnType, page: 1, limit: 100000 });
        rows = r.rows;
      } else {
        const r = await api.adminLineTransactions({ from: range.from, to: range.to, q, category, txn_type: txnType });
        rows = (r.transactions || []).map((t) => ({ member_name: t.member_name || t.name || "Member", member_phone: t.member_phone, category: t.category, txn_type: t.txn_type, amount: t.amount, note: t.note, created_at: t.created_at, transaction_at: t.transaction_at, class_name: t.class_name, court: t.court, bill_total: t.bill_total, bill_paid: t.bill_paid, bill_remaining: t.bill_remaining, bill_status: t.bill_status, payment_type: t.payment_type }));
      }
      if (!rows.length) return toast.error("Tidak ada data untuk diekspor");
      exportLedgerXlsx(rows, range, isOwner ? "Ledger" : "Ledger-Admin");
      toast.success("XLSX terunduh");
    } catch (e) { toast.error(e.message); }
  };

  const sendWa = async (t) => {
    const win = window.open("", "_blank", "noopener");
    try {
      const m = await api.getMember(t.member_id);
      const url = waInvoiceUrl(m, t);
      if (win) win.location.href = url; else window.open(url, "_blank", "noopener");
    } catch (e) { if (win) win.close(); toast.error(e.message); }
  };

  const totalPages = Math.max(1, Math.ceil(state.total / LIMIT));

  return (
    <div>
      <PageHeader eyebrow={isOwner ? "Owner" : "Admin"} title="Ledger"
        subtitle="Semua transaksi keuangan — menjumlah arus kas aktual (tanpa double-count tagihan)."
        right={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={exportXlsx} data-testid="export-xlsx"><FileSpreadsheet className="h-4 w-4" /> Export XLSX</Button>
            {isOwner && <Button variant="outline" onClick={exportCsv} data-testid="export-csv"><Download className="h-4 w-4" /> CSV</Button>}
          </div>
        }
      />

      <Card className="mb-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <input type="date" className="hsc-input w-auto" value={range.from} max={range.to} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
          <span className="text-[#6E6E73]">—</span>
          <input type="date" className="hsc-input w-auto" value={range.to} min={range.from} max={todayISO()} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
          <div className="relative flex-1 min-w-[180px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5A5A5F]" />
            <input className="hsc-input pl-9" placeholder="Cari nama customer" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ledger-search" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Pill active={category === "all"} onClick={() => setCategory("all")}>Semua Kategori</Pill>
          {CATEGORIES.map((c) => <Pill key={c} active={category === c} onClick={() => setCategory(c)}>{c}</Pill>)}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[["all", "Semua Tipe"], ["visit", "Visit"], ["membership", "Membership"]].map(([v, l]) => (
            <Pill key={v} active={txnType === v} onClick={() => setTxnType(v)}>{l}</Pill>
          ))}
        </div>
      </Card>

      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-[#8E8E93]">{state.total} transaksi</div>
        <div className="font-display text-xl text-white" data-testid="ledger-sum">{formatIDR(state.sum)}</div>
      </div>

      {state.loading ? <Loading /> : !state.rows.length ? (
        <EmptyState title="Belum ada transaksi" subtitle="Coba ubah rentang tanggal atau filter." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[#1F1F22]">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0C] text-left text-[11px] uppercase tracking-widest text-[#6E6E73]">
              <tr>
                <th className="px-4 py-3">Tanggal</th>
                <th className="px-4 py-3">Customer</th>
                <th className="hidden px-4 py-3 md:table-cell">Kategori</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Jumlah</th>
                <th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {state.rows.map((t) => (
                <tr key={t.id} className="cursor-pointer border-t border-[#141416] hover:bg-[#0A0A0C]" data-testid="ledger-row" onClick={() => setDetail(t)}>
                  <td className="px-4 py-3 text-[#A1A1A6]">{formatDate(t.transaction_at || t.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="text-white">{t.member_name}</div>
                    {t.note && <div className="text-[11px] text-[#6E6E73]">{t.note}</div>}
                  </td>
                  <td className="hidden px-4 py-3 md:table-cell">
                    <span className="text-[#D1D1D6]">{t.category}</span>
                    {t.txn_type === "membership" && <span className="ml-2 rounded bg-[#141416] px-1.5 py-0.5 text-[10px] text-[#8FD3E8]">MEMBERSHIP</span>}
                  </td>
                  <td className="px-4 py-3 text-center">{t.bill_status ? <SeverityTag value={t.bill_status} /> : <SeverityTag value="PAID" />}</td>
                  <td className="px-4 py-3 text-right font-medium text-white">
                    {formatIDR(t.amount)}
                    {t.bill_remaining > 0 && <div className="text-[11px] text-[#FF8080]">sisa {formatIDR(t.bill_remaining)}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      {t.bill_remaining > 0 && (
                        <button className="flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[11px] font-bold text-black hover:bg-[#E5E5E5]" onClick={(e) => { e.stopPropagation(); setPayRow(t); }} data-testid={`ledger-pay-${t.id}`}><Wallet className="h-3 w-3" /> Pelunasan</button>
                      )}
                      <button className="rounded-lg p-1.5 text-[#8E8E93] hover:bg-[#0d1a12] hover:text-[#25D366]" onClick={(e) => { e.stopPropagation(); sendWa(t); }} title="Kirim invoice WhatsApp" data-testid="ledger-whatsapp"><MessageCircle className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isOwner && totalPages > 1 && (
        <div className="mt-5 flex items-center justify-center gap-3">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm text-[#8E8E93]">Hal {page} / {totalPages}</span>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}

      {detail && (
        <TransactionDetail
          txn={detail}
          canEdit
          canDelete={isOwner}
          onClose={() => setDetail(null)}
          onChanged={load}
        />
      )}

      {payRow && (
        <PaymentModal row={payRow} onClose={() => setPayRow(null)} onDone={() => { setPayRow(null); load(); }} />
      )}
    </div>
  );
}

function PaymentModal({ row, onClose, onDone }) {
  const [bill, setBill] = useState(row.bill_id
    ? null
    : { total: row.bill_total || row.amount, paid: row.bill_paid ?? row.amount, remaining: row.bill_remaining || 0 });
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Transfer");
  const [date, setDate] = useState(todayISO());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (row.bill_id) api.getBill(row.bill_id).then((b) => setBill({ total: b.total, paid: b.paid, remaining: b.remaining })).catch((e) => toast.error(e.message));
  }, [row.bill_id]);

  const remaining = bill?.remaining ?? row.bill_remaining ?? 0;
  const toInt = (v) => parseInt(String(v).replace(/[^0-9]/g, ""), 10) || 0;
  const amt = toInt(amount);

  const submit = async () => {
    if (amt <= 0) return toast.error("Masukkan nominal");
    if (amt > remaining) return toast.error(`Melebihi sisa tagihan (${formatIDR(remaining)})`);
    setBusy(true);
    try {
      const r = await api.addPayment(row.id, { amount: amt, payment_method: method, txn_date: date });
      toast.success(r.bill?.remaining === 0 ? "Lunas — pembayaran tercatat" : `Pembayaran ${formatIDR(amt)} tercatat`);
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open title="Tambah Pembayaran" onClose={() => (busy ? null : onClose())} maxWidth="max-w-md">
      <div className="mb-4 text-sm text-[#8E8E93]">{row.member_name} · {row.category}{row.class_name ? ` · ${row.class_name}` : ""}</div>

      {bill === null ? <Loading /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2 rounded-2xl border border-[#1F1F22] bg-[#0E0E10] p-4 text-center">
            <div><div className="hsc-label">Total</div><div className="mt-1 text-sm text-white" data-testid="pay-total">{formatIDR(bill.total)}</div></div>
            <div><div className="hsc-label">Dibayar</div><div className="mt-1 text-sm text-[#7CD992]">{formatIDR(bill.paid)}</div></div>
            <div><div className="hsc-label">Sisa</div><div className="mt-1 text-sm text-[#FF8080]" data-testid="pay-remaining">{formatIDR(remaining)}</div></div>
          </div>

          <Field label="Nominal Bayar (Rp)">
            <input type="number" className="hsc-input" value={amount} autoFocus placeholder={String(remaining)}
              onChange={(e) => setAmount(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} data-testid="pay-amount" />
            <button type="button" className="mt-1 text-xs text-[#8FD3E8] hover:text-white" onClick={() => setAmount(String(remaining))} data-testid="pay-full">
              Bayar sisa penuh ({formatIDR(remaining)})
            </button>
          </Field>

          <Field label="Tanggal Bayar"><input type="date" className="hsc-input" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} data-testid="pay-date" /></Field>

          <Field label="Metode Pembayaran">
            <div className="flex flex-wrap gap-2">
              {["Transfer", "EDC", "Cash", "QRIS"].map((m) => (
                <Pill key={m} active={method === m} onClick={() => setMethod(m)} data-testid={`pay-method-${m}`}>{m}</Pill>
              ))}
            </div>
          </Field>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" disabled={busy} onClick={onClose}>Batal</Button>
            <Button disabled={busy || amt <= 0} onClick={submit} data-testid="pay-submit">{busy ? "Menyimpan..." : "Simpan Pembayaran"}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
